// src/components/personal/sections/BroadcastsSection/index.tsx
import React, { useState } from 'react';
import { Calendar, Clock, Users } from 'lucide-react';
import { COLORS, TEXT_STYLES, COMPONENT_STYLES } from '@/theme';
import { useResponsive } from '@/hooks/useResponsive';
import { Card, Grid, Flex, Divider } from '@/components/ui/Layout';

interface MessageTemplate {
  id: string;
  name: string;
  content: string;
  createdAt: string;
}

interface Audience {
  id: string;
  name: string;
  filters: {
    orderCount?: number;
    lastOrderDays?: number;
    totalSpent?: number;
  };
  estimatedReach: number;
}

const BroadcastsSection: React.FC = () => {
  const [isScheduled, setIsScheduled] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [selectedAudience, setSelectedAudience] = useState<string>('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const { isMobile } = useResponsive();

  const templates: MessageTemplate[] = [
    {
      id: '1',
      name: 'Скидка 20%',
      content: 'Специально для вас скидка 20% на все меню до конца недели!',
      createdAt: '12.02.2025'
    },
    {
      id: '2',
      name: 'Новое меню',
      content: 'Мы обновили наше меню! Попробуйте новые блюда со скидкой 15%',
      createdAt: '13.02.2025'
    }
  ];

  const audiences: Audience[] = [
    {
      id: '1',
      name: 'Активные клиенты',
      filters: { orderCount: 3, lastOrderDays: 30 },
      estimatedReach: 250
    },
    {
      id: '2',
      name: 'VIP клиенты',
      filters: { totalSpent: 100000 },
      estimatedReach: 50
    }
  ];

  const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTemplate(e.target.value);
  };

  return (
    <Grid gap="md" responsive={true}>
      {/* Создание рассылки */}
      <Card 
        padding={isMobile ? "sm" : "md"} 
        shadow="sm" 
        background="white"
      >
        <div className={COMPONENT_STYLES.card.header}>
          <h2 className={isMobile ? TEXT_STYLES.h3 : TEXT_STYLES.h2}>Создание рассылки</h2>
        </div>
        
        <div className={isMobile ? "p-3" : COMPONENT_STYLES.card.body}>
          {/* Выбор шаблона */}
          <div className="mb-6">
            <label className={`block mb-2 ${TEXT_STYLES.body}`}>
              Шаблон сообщения
            </label>
            <select
              value={selectedTemplate}
              onChange={handleTemplateChange}
              className={COMPONENT_STYLES.input.base}
            >
              <option value="">Выберите шаблон</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          </div>

          {/* Выбор аудитории */}
          <div className="mb-6">
            <label className={`block mb-2 ${TEXT_STYLES.body}`}>
              Аудитория
            </label>
            <select
              value={selectedAudience}
              onChange={(e) => setSelectedAudience(e.target.value)}
              className={COMPONENT_STYLES.input.base}
            >
              <option value="">Выберите аудиторию</option>
              {audiences.map(audience => (
                <option key={audience.id} value={audience.id}>
                  {audience.name} ({audience.estimatedReach} получателей)
                </option>
              ))}
            </select>
          </div>

          {/* Выбор времени отправки */}
          <div className="mb-6">
            <Flex 
              direction={isMobile ? "column" : "row"} 
              gap={isMobile ? "xs" : "md"}
              align="center"
              className="mb-4"
            >
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={!isScheduled}
                  onChange={() => setIsScheduled(false)}
                  className="mr-2"
                  style={{
                    color: COLORS.primary,
                    outlineColor: COLORS.primary, // similar to ring color
                  }}
                />
                <span className={TEXT_STYLES.body}>Отправить сейчас</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={isScheduled}
                  onChange={() => setIsScheduled(true)}
                  className="mr-2"
                  style={{
                    color: COLORS.primary,
                    outlineColor: COLORS.primary, // similar to ring color
                  }}
                />
                <span className={TEXT_STYLES.body}>Запланировать</span>
              </label>
            </Flex>

            {isScheduled && (
              <Flex 
                direction={isMobile ? "column" : "row"}
                gap={isMobile ? "sm" : "md"}
              >
                <div className="flex-1">
                  <label className={`block mb-2 ${TEXT_STYLES.body}`}>
                    Дата
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      className={COMPONENT_STYLES.input.base}
                    />
                    <Calendar className={`w-5 h-5 text-${COLORS.secondary} absolute right-3 top-2.5 pointer-events-none`} />
                  </div>
                </div>
                <div className="flex-1">
                  <label className={`block mb-2 ${TEXT_STYLES.body}`}>
                    Время
                  </label>
                  <div className="relative">
                    <input
                      type="time"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      className={COMPONENT_STYLES.input.base}
                    />
                    <Clock className={`w-5 h-5 text-${COLORS.secondary} absolute right-3 top-2.5 pointer-events-none`} />
                  </div>
                </div>
              </Flex>
            )}
          </div>

          <button className={`${COMPONENT_STYLES.button.primary} ${isMobile ? "w-full" : ""}`}>
            {isScheduled ? 'Запланировать рассылку' : 'Начать рассылку'}
          </button>
        </div>
      </Card>

      {/* Шаблоны сообщений */}
      <Card 
        padding={isMobile ? "sm" : "md"} 
        shadow="sm" 
        background="white"
      >
        <div className={COMPONENT_STYLES.card.header}>
          <Flex justify="between" align="center">
            <h2 className={isMobile ? TEXT_STYLES.h3 : TEXT_STYLES.h2}>Шаблоны сообщений</h2>
            <button className={`text-${COLORS.primary} hover:text-${COLORS.primary}/80 text-sm`}>
              + Создать шаблон
            </button>
          </Flex>
        </div>
        
        <div className={isMobile ? "p-3" : COMPONENT_STYLES.card.body}>
          <Grid gap="sm" cols={1}>
            {templates.map(template => (
              <Card 
                key={template.id} 
                padding="sm"
                shadow="none"
                background="white"
                className={`border border-${COLORS.secondary}/20 rounded-lg`}
              >
                <Flex 
                  direction={isMobile ? "column" : "row"} 
                  justify="between" 
                  align={isMobile ? "start" : "center"}
                  className="mb-2"
                >
                  <h3 className={TEXT_STYLES.body}>{template.name}</h3>
                  <span className={`${TEXT_STYLES.small} text-${COLORS.secondary} ${isMobile ? "mt-1" : ""}`}>
                    {template.createdAt}
                  </span>
                </Flex>
                <p className={`${TEXT_STYLES.small} text-${COLORS.text}`}>
                  {template.content}
                </p>
              </Card>
            ))}
          </Grid>
        </div>
      </Card>

      {/* Аудитории */}
      <Card 
        padding={isMobile ? "sm" : "md"} 
        shadow="sm" 
        background="white"
      >
        <div className={COMPONENT_STYLES.card.header}>
          <Flex justify="between" align="center">
            <h2 className={isMobile ? TEXT_STYLES.h3 : TEXT_STYLES.h2}>Аудитории</h2>
            <button className={`text-${COLORS.primary} hover:text-${COLORS.primary}/80 text-sm`}>
              + Создать аудиторию
            </button>
          </Flex>
        </div>
        
        <div className={isMobile ? "p-3" : COMPONENT_STYLES.card.body}>
          <Grid gap="sm" cols={1}>
            {audiences.map(audience => (
              <Card 
                key={audience.id}
                padding="sm"
                shadow="none"
                background="white" 
                className={`border border-${COLORS.secondary}/20 rounded-lg`}
              >
                <Flex 
                  direction={isMobile ? "column" : "row"} 
                  justify="between" 
                  align={isMobile ? "start" : "center"}
                  className="mb-2"
                >
                  <div>
                    <h3 className={TEXT_STYLES.body}>{audience.name}</h3>
                    <Flex align="center" className={`${TEXT_STYLES.small} text-${COLORS.secondary} mt-1`}>
                      <Users className="w-4 h-4 mr-1" />
                      {audience.estimatedReach} получателей
                    </Flex>
                  </div>
                  <button className={`text-${COLORS.primary} hover:text-${COLORS.primary}/80 ${TEXT_STYLES.small} ${isMobile ? "mt-2" : ""}`}>
                    Редактировать
                  </button>
                </Flex>
                <Divider margin="sm" />
                <div>
                  <div className={`${TEXT_STYLES.small} text-${COLORS.text}`}>
                    Фильтры:
                    <Flex 
                      direction={isMobile ? "column" : "row"} 
                      wrap={!isMobile}
                      gap="xs"
                      className="mt-1"
                    >
                      {audience.filters.orderCount && (
                        <span className={`${isMobile ? "" : "ml-2"}`}>Мин. заказов: {audience.filters.orderCount}</span>
                      )}
                      {audience.filters.lastOrderDays && (
                        <span className={`${isMobile ? "" : "ml-2"}`}>Последний заказ: {audience.filters.lastOrderDays} дней</span>
                      )}
                      {audience.filters.totalSpent && (
                        <span className={`${isMobile ? "" : "ml-2"}`}>Мин. сумма покупок: {audience.filters.totalSpent.toLocaleString()} ₸</span>
                      )}
                    </Flex>
                  </div>
                </div>
              </Card>
            ))}
          </Grid>
        </div>
      </Card>
    </Grid>
  );
};

export default BroadcastsSection;